/**
 * "How Fomo-Fi works" (doc 3 E1-E16; Paul 2026-10-28): which walkthrough is open, as a
 * tiny module store, so any component can open it with `useWalkthrough()` and one
 * `<WalkthroughHost />` renders it, without wrapping the app in another provider.
 *
 * Nothing here opens it on its own (A3): the store starts closed and only
 * `openWalkthrough` sets a session.
 */
import { useSyncExternalStore } from "react";

export interface WalkthroughSession {
  /** The step to show first. */
  step: number;
  /** The page it was opened over, for "Back to [page]" / "Done · back to [page]". */
  from: string;
  /** Where this opener starts with nothing saved: the page's step, or a deep link's own
   *  step. Closing saves only past it (addendum R6). Defaults to `step`. */
  startStep?: number;
}

let session: WalkthroughSession | null = null;
/** The step saved from a partway close (1–10), or null. Published by WalkthroughHost:
 *  the account's flag when signed in, this browser's before there's an account. */
let savedStep: number | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function openWalkthroughSession(next: WalkthroughSession): void {
  session = next;
  emit();
}

export function closeWalkthroughSession(): void {
  session = null;
  emit();
}

export function setWalkthroughSavedStep(step: number | null): void {
  if (step === savedStep) return;
  savedStep = step;
  emit();
}

export function getWalkthroughSavedStep(): number | null {
  return savedStep;
}

export function useWalkthroughSession(): WalkthroughSession | null {
  return useSyncExternalStore(
    subscribe,
    () => session,
    // The app never renders on a server; the same snapshot keeps static renders (tests)
    // honest.
    () => session,
  );
}

export function useWalkthroughSavedStep(): number | null {
  return useSyncExternalStore(
    subscribe,
    () => savedStep,
    () => savedStep,
  );
}
